import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from 'react';
import { UIState, ModalState, ViewParams } from '../types';

// ============ Page title (#72) ============
/**
 * What the blue top bar shows for the page currently on screen.
 *
 * `ownerId` is the reason this is an object rather than a bare string. Pages
 * set the title on mount and clear it on unmount, and React runs the incoming
 * page's mount *before* the outgoing page's cleanup. An unguarded clear would
 * therefore wipe the title the new page had just claimed, leaving the bar
 * blank on exactly the page the user navigated to. Clearing is scoped to the
 * owner that set it, so a late cleanup from a page that is already gone is a
 * no-op.
 */
export interface PageTitle {
  ownerId: string;
  title: string;
  subtitle?: string;
}

// ============ State Shape ============
interface UIContextState {
  activeView: UIState['activeView'];
  viewParams?: ViewParams;
  sidebarCollapsed: boolean;
  theme: UIState['theme'];
  modalState: ModalState;
  errors: string[];
  pageTitle: PageTitle | null;
}

// ============ Actions ============
type UIAction =
  | {
      type: 'SET_ACTIVE_VIEW';
      payload: { view: UIState['activeView']; params?: ViewParams };
    }
  | { type: 'CLEAR_VIEW_PARAMS' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_THEME'; payload: UIState['theme'] }
  | { type: 'SET_MODAL'; payload: ModalState }
  | { type: 'CLOSE_MODAL' }
  | { type: 'ADD_ERROR'; payload: string }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'SET_PAGE_TITLE'; payload: PageTitle }
  | { type: 'CLEAR_PAGE_TITLE'; payload: { ownerId: string } };

// ============ Reducer ============
const initialState: UIContextState = {
  activeView: 'dashboard',
  viewParams: undefined,
  sidebarCollapsed: false,
  theme: 'system',
  modalState: {
    type: null,
    data: null,
  },
  errors: [],
  pageTitle: null,
};

function uiReducer(state: UIContextState, action: UIAction): UIContextState {
  switch (action.type) {
    case 'SET_ACTIVE_VIEW':
      return {
        ...state,
        activeView: action.payload.view,
        viewParams: action.payload.params,
      };
    case 'CLEAR_VIEW_PARAMS':
      return { ...state, viewParams: undefined };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'SET_MODAL':
      return { ...state, modalState: action.payload };
    case 'CLOSE_MODAL':
      return { ...state, modalState: { type: null, data: null } };
    case 'ADD_ERROR':
      return { ...state, errors: [...state.errors, action.payload] };
    case 'CLEAR_ERRORS':
      return { ...state, errors: [] };
    case 'SET_PAGE_TITLE':
      return { ...state, pageTitle: action.payload };
    case 'CLEAR_PAGE_TITLE':
      // Only the current owner may clear. See the PageTitle doc comment.
      return state.pageTitle?.ownerId === action.payload.ownerId
        ? { ...state, pageTitle: null }
        : state;
    default:
      return state;
  }
}

// ============ Context ============
interface UIContextType extends UIContextState {
  setActiveView: (view: UIState['activeView'], params?: ViewParams) => void;
  clearViewParams: () => void;
  toggleSidebar: () => void;
  setTheme: (theme: UIState['theme']) => void;
  setModal: (modal: ModalState) => void;
  closeModal: () => void;
  addError: (error: string) => void;
  clearErrors: () => void;
  /** #72: prefer the `usePageTitle` hook — it owns the id and the cleanup. */
  setPageTitle: (pageTitle: PageTitle) => void;
  clearPageTitle: (ownerId: string) => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

// ============ Provider ============
export function UIProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  const setActiveView = useCallback(
    (view: UIState['activeView'], params?: ViewParams) => {
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: { view, params } });
    },
    []
  );

  const clearViewParams = useCallback(() => {
    dispatch({ type: 'CLEAR_VIEW_PARAMS' });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const setTheme = useCallback((theme: UIState['theme']) => {
    dispatch({ type: 'SET_THEME', payload: theme });
  }, []);

  const setModal = useCallback((modal: ModalState) => {
    dispatch({ type: 'SET_MODAL', payload: modal });
  }, []);

  const closeModal = useCallback(() => {
    dispatch({ type: 'CLOSE_MODAL' });
  }, []);

  const addError = useCallback((error: string) => {
    dispatch({ type: 'ADD_ERROR', payload: error });
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
  }, []);

  const setPageTitle = useCallback((pageTitle: PageTitle) => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: pageTitle });
  }, []);

  const clearPageTitle = useCallback((ownerId: string) => {
    dispatch({ type: 'CLEAR_PAGE_TITLE', payload: { ownerId } });
  }, []);

  const value = {
    ...state,
    setActiveView,
    clearViewParams,
    toggleSidebar,
    setTheme,
    setModal,
    closeModal,
    addError,
    clearErrors,
    setPageTitle,
    clearPageTitle,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

// ============ Hook ============
export function useUI() {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}
